import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import usePageTitle from "../../hooks/usePageTitle";
import { apiGet } from "../../api";
import SubmissionsTab from "./SubmissionsTab";
import SuppliersTab from "./SuppliersTab";
import SettingsTab from "./SettingsTab";
import ReviewQueueTab from "./ReviewQueueTab";

/**
 * Internal-team view of the Supplier Submission Portal.
 *
 * Four tabs:
 *   1. Submissions   — approval queue (review/approve/reject + WMS/Kraken confirmation)
 *   2. Review Queue  — submissions held back by the SKU fuzzy-match check; per-row
 *                      reconcile (approve as new OR map to existing canonical SKU)
 *   3. Suppliers     — active supplier list + portal-URL + fast-track token management
 *   4. Settings      — add supplier, categories CRUD, category owners, records updater
 *
 * Routed at /portal-admin (gated by Google Auth in App.js).
 */
export default function PortalAdmin() {
  usePageTitle("Supplier Portal");
  // Deep-link support: ?sub=<id> opens a submission's approval directly (e.g. from
  // the manager digest email); ?ptab=review-queue selects the internal tab.
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const focusSubmissionId = params.get("sub") || null;
  const initialTab = params.get("ptab") || (focusSubmissionId ? "submissions" : "submissions");
  const [tab, setTab] = useState(initialTab);
  const [queueCount, setQueueCount] = useState(0);

  const refreshCount = useCallback(async () => {
    try {
      const data = await apiGet("/portal/admin/review-queue/count");
      setQueueCount(Number(data?.count) || 0);
    } catch {
      /* badge is best-effort */
    }
  }, []);

  // Initial fetch + refresh whenever the user comes back to the queue tab.
  useEffect(() => { refreshCount(); }, [refreshCount]);
  useEffect(() => {
    if (tab === "review-queue") refreshCount();
  }, [tab, refreshCount]);

  return (
    <div style={{ padding: "0 1.5rem" }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ fontSize: "1.75rem", fontWeight: "700", color: "var(--octopus-ice)", marginBottom: "0.5rem" }}>
          Supplier Submission Portal
        </h1>
        <p style={{ color: "var(--octopus-voltage)", opacity: 0.8, fontSize: "0.9rem" }}>
          Review price updates submitted by suppliers and manage portal access.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="portal-admin-tabs" style={{ display: "flex", flexWrap: "wrap", height: "auto", width: "fit-content", maxWidth: "100%", justifyContent: "flex-start" }}>
          <TabsTrigger value="submissions" data-testid="tab-submissions">Submissions</TabsTrigger>
          <TabsTrigger value="review-queue" data-testid="tab-review-queue">
            Review Queue
            {queueCount > 0 && (
              <span
                data-testid="review-queue-badge"
                style={{
                  marginLeft: "0.5rem",
                  background: "#fb923c",
                  color: "#100030",
                  borderRadius: "999px",
                  padding: "0.05rem 0.45rem",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  lineHeight: 1.4,
                }}
              >
                {queueCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="submissions"><SubmissionsTab focusId={focusSubmissionId} /></TabsContent>
        <TabsContent value="review-queue"><ReviewQueueTab onResolved={refreshCount} /></TabsContent>
        <TabsContent value="suppliers"><SuppliersTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
